/**
 * Customer "Request changes" widget. One trigger button → modal with 3 tabs:
 *   - Cancel booking
 *   - Modify extras (add-ons)
 *   - Swap insurance
 *
 * Submits to `POST /bookings/{id}/change-requests`. Existing pending requests
 * are shown inline above the form so the customer can't double-fire.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2, Pencil, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import Money from "@/components/Money";

const KIND_LABEL = {
  cancel: "Cancellation",
  update_extras: "Extras change",
  update_insurance: "Insurance change",
};

const STATUS_TONE = {
  pending: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", label: "Awaiting host" },
  approved: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", label: "Approved" },
  denied: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-800", label: "Declined" },
  withdrawn: { bg: "bg-neutral-50", border: "border-neutral-200", text: "text-neutral-600", label: "Withdrawn" },
};

export default function RequestChangesMenu({ booking, onChanged }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("cancel");
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(false);

  // Form state
  const [note, setNote] = useState("");
  const [pickedAddons, setPickedAddons] = useState([]);
  const [pickedInsurance, setPickedInsurance] = useState("__none__");

  const car = booking?.car;
  const addOns = car?.add_ons || [];
  const insurance = car?.insurance_plans || [];
  const days = booking?.days || 1;

  // Seed form state from the booking so customers see their CURRENT selection by default.
  useEffect(() => {
    setPickedAddons((booking?.add_ons || []).map((a) => a.id).filter(Boolean));
    setPickedInsurance(booking?.insurance_plan_id || "__none__");
  }, [booking?.id]);

  const loadRequests = async () => {
    if (!booking?.id) return;
    try {
      const { data } = await api.get(`/bookings/${booking.id}/change-requests`);
      setRequests(data || []);
    } catch (e) {
      console.error("change-requests load failed:", e);
    }
  };

  useEffect(() => {
    if (open) loadRequests();
  }, [open, booking?.id]);

  const pending = requests.find((r) => r.status === "pending");
  const history = requests.filter((r) => r.status !== "pending").slice(0, 4);

  // Live delta estimates so customers see what they'll be charged BEFORE submitting.
  const extrasDelta = useMemo(() => {
    const newFee = pickedAddons.reduce((sum, id) => {
      const a = addOns.find((x) => x.id === id);
      if (!a) return sum;
      return sum + (a.pricing === "per_day" ? Number(a.price || 0) * days : Number(a.price || 0));
    }, 0);
    return Number((newFee - Number(booking?.add_ons_fee || 0)).toFixed(2));
  }, [pickedAddons, addOns, days, booking?.add_ons_fee]);

  const insuranceDelta = useMemo(() => {
    const planId = pickedInsurance === "__none__" ? null : pickedInsurance;
    const plan = insurance.find((p) => p.id === planId);
    const newFee = plan ? Number(plan.price_per_day || 0) * days : 0;
    return Number((newFee - Number(booking?.insurance_fee || 0)).toFixed(2));
  }, [pickedInsurance, insurance, days, booking?.insurance_fee]);

  const submit = async () => {
    setBusy(true);
    try {
      let body = { customer_note: note.trim() || null };
      if (tab === "cancel") {
        body.kind = "cancel";
      } else if (tab === "update_extras") {
        body.kind = "update_extras";
        body.add_on_ids = pickedAddons;
      } else if (tab === "update_insurance") {
        body.kind = "update_insurance";
        body.insurance_plan_id = pickedInsurance === "__none__" ? null : pickedInsurance;
      }
      await api.post(`/bookings/${booking.id}/change-requests`, body);
      toast.success("Request sent — the host will review it shortly");
      setNote("");
      setOpen(false);
      await loadRequests();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (reqId) => {
    setBusy(true);
    try {
      await api.post(`/booking-changes/${reqId}/withdraw`);
      toast.success("Request withdrawn");
      await loadRequests();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Booking must be confirmed/pending_payment for the trigger to be active.
  const eligible = ["confirmed", "pending_payment"].includes(booking?.status);

  return (
    <>
      <Button
        size="sm"
        variant="ghost"
        onClick={() => setOpen(true)}
        disabled={!eligible}
        className="rounded-full text-xs h-7 text-blue-700 hover:bg-blue-50"
        title={eligible ? "Request changes from the host" : "Only active bookings can be changed"}
        data-testid={`request-changes-${booking?.id}`}
      >
        <Pencil className="h-3.5 w-3.5 mr-1" /> Request changes
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="request-changes-dialog">
          <DialogHeader>
            <DialogTitle>Request changes</DialogTitle>
            <DialogDescription>
              The host approves every change. Refunds or extra charges are settled separately by the host once approved.
            </DialogDescription>
          </DialogHeader>

          {/* Pending request blocks the form */}
          {pending && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="pending-request-banner">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
              <div className="flex-1 min-w-0">
                <div className="font-medium">{KIND_LABEL[pending.kind]} pending</div>
                <div className="text-xs opacity-80">Submitted {new Date(pending.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <Button size="sm" variant="ghost" onClick={() => withdraw(pending.id)} disabled={busy} className="rounded-full text-xs h-7" data-testid="withdraw-pending">
                <X className="h-3 w-3 mr-1" />Withdraw
              </Button>
            </div>
          )}

          {!pending && (
            <Tabs value={tab} onValueChange={setTab} className="mt-2">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="cancel" data-testid="tab-cancel">Cancel</TabsTrigger>
                <TabsTrigger value="update_extras" data-testid="tab-extras">Extras</TabsTrigger>
                <TabsTrigger value="update_insurance" data-testid="tab-insurance">Insurance</TabsTrigger>
              </TabsList>

              <TabsContent value="cancel" className="space-y-3 pt-3">
                <p className="text-sm text-neutral-600">
                  Ask the host to cancel this booking. Any refund of your booking fee or deposit is handled by the host separately.
                </p>
              </TabsContent>

              <TabsContent value="update_extras" className="space-y-3 pt-3">
                {addOns.length === 0 ? (
                  <p className="text-sm text-neutral-500" data-testid="extras-empty">
                    This host hasn't listed optional extras for this vehicle yet. Describe what you need
                    (e.g. a child seat) in the note below, or message the host directly.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600">Tick the extras you want on your booking. The host will confirm the new total.</p>
                    <div className="space-y-2" data-testid="extras-picker">
                      {addOns.map((a) => {
                        const checked = pickedAddons.includes(a.id);
                        return (
                          <label key={a.id} className="flex items-center gap-3 rounded-xl border border-border p-3 hover:bg-neutral-50 cursor-pointer" data-testid={`extras-row-${a.id}`}>
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => setPickedAddons((prev) => (v ? [...prev, a.id] : prev.filter((x) => x !== a.id)))}
                              data-testid={`extras-check-${a.id}`}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">{a.name}</div>
                              <div className="text-xs text-neutral-500">
                                <Money amount={a.price} /> {a.pricing === "per_day" ? `/day · × ${days} days` : "one-time"}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    <DeltaSummary delta={extrasDelta} />
                  </>
                )}
              </TabsContent>

              <TabsContent value="update_insurance" className="space-y-3 pt-3">
                {insurance.length === 0 ? (
                  <p className="text-sm text-neutral-500" data-testid="insurance-empty">
                    This host hasn't listed insurance upgrades for this vehicle yet — minimum third-party coverage is
                    always included. Ask the host about extra coverage via the note below or a direct message.
                  </p>
                ) : (
                  <>
                    <p className="text-sm text-neutral-600">Pick a plan or remove insurance entirely (minimum coverage stays included).</p>
                    <RadioGroup value={pickedInsurance} onValueChange={setPickedInsurance} className="space-y-2" data-testid="insurance-picker">
                      <label className="flex items-center gap-3 rounded-xl border border-border p-3 hover:bg-neutral-50 cursor-pointer" data-testid="insurance-row-none">
                        <RadioGroupItem value="__none__" id="ins-none" />
                        <div className="flex-1 min-w-0">
                          <Label htmlFor="ins-none" className="text-sm font-medium cursor-pointer">Minimum coverage only</Label>
                          <div className="text-xs text-neutral-500">Third-party liability insurance (included, no extra fee)</div>
                        </div>
                      </label>
                      {insurance.map((p) => (
                        <label key={p.id} className="flex items-center gap-3 rounded-xl border border-border p-3 hover:bg-neutral-50 cursor-pointer" data-testid={`insurance-row-${p.id}`}>
                          <RadioGroupItem value={p.id} id={`ins-${p.id}`} />
                          <div className="flex-1 min-w-0">
                            <Label htmlFor={`ins-${p.id}`} className="text-sm font-medium cursor-pointer">{p.name}</Label>
                            <div className="text-xs text-neutral-500">
                              <Money amount={p.price_per_day} />/day · × {days} days = <Money amount={Number(p.price_per_day || 0) * days} />
                            </div>
                          </div>
                        </label>
                      ))}
                    </RadioGroup>
                    <DeltaSummary delta={insuranceDelta} />
                  </>
                )}
              </TabsContent>
            </Tabs>
          )}

          {!pending && (
            <div className="mt-3">
              <Label className="text-xs uppercase tracking-wider text-neutral-500">Note to host (optional)</Label>
              <Textarea
                rows={2}
                className="rounded-xl mt-1"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Tell the host why you're requesting this change…"
                maxLength={500}
                data-testid="request-changes-note"
              />
            </div>
          )}

          {history.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold mb-1.5">Recent requests</div>
              <div className="space-y-1.5">
                {history.map((r) => {
                  const tone = STATUS_TONE[r.status] || STATUS_TONE.withdrawn;
                  return (
                    <div key={r.id} className={`rounded-lg ${tone.bg} ${tone.border} border px-2.5 py-1.5 text-xs flex items-center justify-between gap-2`} data-testid={`history-row-${r.id}`}>
                      <span className="truncate">
                        <span className={`font-medium ${tone.text}`}>{KIND_LABEL[r.kind]}</span>
                        {r.vendor_note && <span className="text-neutral-600"> · "{r.vendor_note}"</span>}
                      </span>
                      <span className={`shrink-0 font-semibold ${tone.text}`}>{tone.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full" data-testid="request-changes-cancel">Close</Button>
            {!pending && (
              <Button
                onClick={submit}
                disabled={busy}
                className="rounded-full bg-blue-600 hover:bg-blue-700"
                data-testid="request-changes-submit"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Pencil className="h-3.5 w-3.5 mr-1.5" />}
                Send request to host
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function DeltaSummary({ delta }) {
  if (Math.abs(delta) < 0.01) return null;
  const sign = delta > 0 ? "+" : "";
  const tone = delta > 0 ? "text-amber-700" : "text-emerald-700";
  return (
    <div className={`text-xs ${tone} font-medium`} data-testid="delta-summary">
      Estimated change to your total: <strong>{sign}<Money amount={delta} /></strong>
      <span className="text-neutral-500 font-normal"> · the host confirms the final amount</span>
    </div>
  );
}
